import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Calculator, Lightbulb } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import MathText from '@/components/MathText';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// Topic label mapping
const TOPIC_LABELS = {
  'Algebra': 'Algebra',
  'AdvancedMath': 'Advanced Math',
  'GeoTrig': 'Geometry & Trig',
  'PSD': 'Problem Solving'
};

export default function PersonalizedPractice({ user, setUser }) {
  const navigate = useNavigate();
  
  const [sessionData, setSessionData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorType, setCalculatorType] = useState('scientific');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [hints, setHints] = useState({});
  const [showHint, setShowHint] = useState({});

  // Load session from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('personalizedPracticeSession');
    if (stored) {
      const data = JSON.parse(stored);
      setSessionData(data);
      setQuestions(data.questions || []);
      document.title = 'Personalized Practice | Practice4SAT';
    } else {
      navigate('/practice');
    }
  }, [navigate]);

  // Track time spent on each question
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestionIndex]);

  const handleExit = () => {
    sessionStorage.removeItem('personalizedPracticeSession');
    navigate('/practice');
  };

  const handleAnswer = (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    
    setAnswers({
      ...answers,
      [currentQuestion.id]: { answer, time_spent: timeSpent }
    });
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setQuestionStartTime(Date.now());
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Build submission data
      const submissionAnswers = {};
      const timeSpent = {};
      
      questions.forEach(q => {
        submissionAnswers[q.id] = answers[q.id]?.answer || '';
        timeSpent[q.id] = answers[q.id]?.time_spent || 0;
      });

      const response = await axios.post(`${API}/personalized-practice/submit`, {
        session_id: sessionData.session_id,
        answers: submissionAnswers,
        time_spent: timeSpent
      });
      
      // Fetch detailed question info including explanations
      const questionDetails = await Promise.all(
        questions.map(async (q) => {
          try {
            const res = await axios.get(`${API}/personalized-practice/question/${q.id}`);
            const userAnswer = answers[q.id]?.answer || '';
            const isCorrect = userAnswer.toUpperCase() === (res.data.correct_answer || '').toUpperCase();
            return {
              ...res.data,
              user_answer: userAnswer,
              is_correct: isCorrect
            };
          } catch (err) {
            // Fallback to basic question data
            return {
              ...q,
              user_answer: answers[q.id]?.answer || '',
              is_correct: false
            };
          }
        })
      );
      
      setResults({
        ...response.data,
        questionDetails
      });
      setShowResults(true);
      sessionStorage.removeItem('personalizedPracticeSession');
    } catch (error) {
      console.error('Error submitting results:', error);
      toast.error('Failed to submit results');
    } finally {
      setLoading(false);
    }
  };

  if (!sessionData || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.id];

  // Exit button for header
  const exitButton = (
    <Button 
      onClick={() => setShowExitDialog(true)}
      variant="outline"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
      data-testid="exit-practice-btn"
    >
      <X size={20} />
      Exit Practice
    </Button>
  );

  // Results Screen
  if (showResults && results) {
    const getScoreColor = (acc) => {
      if (acc >= 80) return '#10b981';
      if (acc >= 60) return '#f59e0b';
      return '#ef4444';
    };

    const getScoreEmoji = (acc) => {
      if (acc >= 80) return '🎉';
      if (acc >= 60) return '👍';
      return '💪';
    };

    // Back button for header
    const backButton = (
      <Button 
        onClick={() => navigate('/practice')}
        variant="outline"
        style={{ 
          background: 'white',
          color: '#0030b3',
          border: 'none'
        }}
      >
        Back to Practice
      </Button>
    );

    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header 
          variant="minimal" 
          logoClickPath="/practice"
          rightContent={backButton}
        />
        
        <div style={{ flex: 1, background: '#f5f5f5' }}>
          <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
            {/* Title */}
            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0030b3', marginBottom: '1rem', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
              Practice Results
            </h1>

            {/* Results Card */}
            <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                  {getScoreEmoji(results.overall_accuracy || 0)}
                </div>
                <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', color: '#1a202c' }}>
                  Practice Complete!
                </h2>
              </div>

              {/* Score Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1a202c', marginBottom: '0.5rem' }}>
                    {results.total_questions || 0}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Arial, sans-serif' }}>Total Questions</div>
                </div>
                <div style={{ background: '#d1fae5', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981', marginBottom: '0.5rem' }}>
                    {results.total_correct || 0}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Arial, sans-serif' }}>Correct</div>
                </div>
                <div style={{ background: '#e0e7ff', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: getScoreColor(results.overall_accuracy || 0), marginBottom: '0.5rem' }}>
                    {results.overall_accuracy || 0}%
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', fontFamily: 'Arial, sans-serif' }}>Accuracy</div>
                </div>
              </div>

              {/* Topic Breakdown */}
              {results.topic_accuracy && Object.keys(results.topic_accuracy).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>Performance by Topic</h3>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {Object.entries(results.topic_accuracy).map(([topic, accuracy]) => (
                      <div key={topic} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ width: '120px', fontSize: '0.875rem', fontWeight: '500', color: '#333' }}>
                          {TOPIC_LABELS[topic] || topic}
                        </span>
                        <div style={{ flex: 1, background: '#e5e5e5', borderRadius: '4px', height: '8px' }}>
                          <div
                            style={{
                              width: `${accuracy}%`,
                              height: '100%',
                              borderRadius: '4px',
                              background: accuracy >= 70 ? '#22c55e' : '#ef4444'
                            }}
                          />
                        </div>
                        <span style={{ 
                          width: '50px', 
                          textAlign: 'right', 
                          fontSize: '0.875rem', 
                          fontWeight: '600',
                          color: accuracy >= 70 ? '#166534' : '#991b1b'
                        }}>
                          {accuracy}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <Button
                  onClick={() => navigate('/practice')}
                  style={{ background: '#0030b3', color: 'white', padding: '0.75rem 2rem', fontSize: '1rem' }}
                  data-testid="back-to-practice-btn"
                >
                  Back to Practice
                </Button>
              </div>
            </div>

            {/* Question Review with Explanations */}
            {results.questionDetails && results.questionDetails.length > 0 && (
              <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', fontFamily: 'Arial, sans-serif' }}>
                  Question Review & Explanations
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  {results.questionDetails.map((result, idx) => (
                    <div 
                      key={result.id || idx} 
                      style={{ 
                        padding: '1.5rem', 
                        borderRadius: '8px', 
                        border: `2px solid ${result.is_correct ? '#10b981' : '#ef4444'}`,
                        background: 'white'
                      }}
                    >
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '600', fontFamily: 'Arial, sans-serif' }}>Question {idx + 1}</span>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {result.topic && (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              background: '#e6f0ff',
                              color: '#0030b3'
                            }}>
                              {TOPIC_LABELS[result.topic] || result.topic}
                            </span>
                          )}
                          <span style={{ 
                            padding: '0.5rem 1rem', 
                            borderRadius: '9999px', 
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            background: result.is_correct ? '#10b981' : '#ef4444',
                            color: 'white'
                          }}>
                            {result.is_correct ? '✓ Correct' : '✗ Incorrect'}
                          </span>
                        </div>
                      </div>

                      {/* Question Text */}
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                          <MathText>{result.question}</MathText>
                        </div>

                        {/* Image if present */}
                        {result.image_url && (
                          <img 
                            src={result.image_url} 
                            alt="Question diagram" 
                            style={{ 
                              maxWidth: 'min(400px, 100%)', 
                              width: 'auto',
                              height: 'auto',
                              marginBottom: '1rem', 
                              borderRadius: '4px',
                              display: 'block'
                            }}
                          />
                        )}
                      </div>

                      {/* Answer Choices */}
                      {result.options && result.options.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <div style={{ display: 'grid', gap: '0.5rem' }}>
                            {result.options.map((optionText, optIdx) => {
                              const choice = String.fromCharCode(65 + optIdx); // A, B, C, D
                              const isUserAnswer = result.user_answer === choice;
                              const isCorrectAnswer = result.correct_answer === choice;
                              
                              let borderColor = '#e5e7eb';
                              let backgroundColor = 'white';
                              
                              if (isCorrectAnswer) {
                                borderColor = '#10b981';
                                backgroundColor = '#f0fdf4';
                              } else if (isUserAnswer && !result.is_correct) {
                                borderColor = '#ef4444';
                                backgroundColor = '#fef2f2';
                              }
                              
                              return (
                                <div
                                  key={choice}
                                  style={{
                                    padding: '0.75rem',
                                    borderRadius: '6px',
                                    border: `2px solid ${borderColor}`,
                                    background: backgroundColor,
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                  }}
                                >
                                  <span style={{ fontWeight: '600', marginRight: '0.75rem', color: '#333' }}>{choice}</span>
                                  <div style={{ flex: 1 }}>
                                    <MathText>{optionText}</MathText>
                                  </div>
                                  {isCorrectAnswer && (
                                    <span style={{ marginLeft: '0.5rem', color: '#10b981', fontWeight: '600' }}>✓</span>
                                  )}
                                  {isUserAnswer && !result.is_correct && (
                                    <span style={{ marginLeft: '0.5rem', color: '#ef4444', fontWeight: '600' }}>✗</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Answer Summary */}
                      <div style={{ 
                        padding: '1rem', 
                        background: '#f9fafb', 
                        borderRadius: '6px',
                        marginBottom: '1rem',
                        fontSize: '0.9rem'
                      }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>Your answer:</strong> <span style={{ color: result.is_correct ? '#10b981' : '#ef4444', fontWeight: '600' }}>{result.user_answer || 'Not answered'}</span>
                        </div>
                        <div>
                          <strong>Correct answer:</strong> <span style={{ color: '#10b981', fontWeight: '600' }}>{result.correct_answer}</span>
                        </div>
                      </div>

                      {/* Explanation */}
                      {result.explanation && (
                        <div style={{ 
                          padding: '1.25rem', 
                          background: '#eff6ff', 
                          borderLeft: '4px solid #3b82f6',
                          borderRadius: '6px'
                        }}>
                          <h4 style={{ 
                            fontSize: '0.95rem', 
                            fontWeight: '600', 
                            marginBottom: '0.75rem',
                            color: '#1e40af',
                            fontFamily: 'Arial, sans-serif'
                          }}>
                            Explanation:
                          </h4>
                          <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#1e3a8a' }}>
                            <MathText>{result.explanation}</MathText>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Question Screen - MATCHES PRACTICE SESSION EXACTLY
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="minimal" 
        onLogoClick={() => setShowExitDialog(true)}
        rightContent={exitButton}
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
          {/* Title */}
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0030b3', marginBottom: '1rem', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
            Personalized Practice
          </h1>

          <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            {/* Question Navigation */}
            <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {questions.map((q, idx) => (
                <button
                  key={q.id}
                  onClick={() => goToQuestion(idx)}
                  data-testid={`question-nav-${idx + 1}`}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '4px',
                    border: `2px solid ${idx === currentQuestionIndex ? '#0030b3' : '#ddd'}`,
                    background: answers[q.id] ? '#0030b3' : 'white',
                    color: answers[q.id] ? 'white' : (idx === currentQuestionIndex ? '#0030b3' : '#666'),
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            {/* Question */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0, fontFamily: 'Arial, sans-serif' }}>
                  Question {currentQuestionIndex + 1}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {currentQuestion?.topic && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: '#e6f0ff',
                      color: '#0030b3'
                    }}>
                      {TOPIC_LABELS[currentQuestion.topic] || currentQuestion.topic}
                    </span>
                  )}
                  {currentQuestion?.difficulty && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      background: currentQuestion.difficulty === 'easy' ? '#dcfce7' :
                                 currentQuestion.difficulty === 'medium' ? '#fef3c7' : '#fee2e2',
                      color: currentQuestion.difficulty === 'easy' ? '#166534' :
                             currentQuestion.difficulty === 'medium' ? '#92400e' : '#991b1b'
                    }}>
                      {currentQuestion.difficulty}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                <MathText>{currentQuestion?.question}</MathText>
              </div>

              {currentQuestion?.image_url && (
                <img 
                  src={currentQuestion.image_url} 
                  alt="Question diagram" 
                  style={{ 
                    maxWidth: 'min(400px, 100%)', 
                    width: 'auto',
                    height: 'auto',
                    marginBottom: '1.5rem', 
                    borderRadius: '4px',
                    display: 'block'
                  }}
                />
              )}
            </div>

            {/* Answer Options */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {(currentQuestion?.options || []).map((optionText, idx) => {
                  const choice = String.fromCharCode(65 + idx); // A, B, C, D
                  return (
                    <button
                      key={choice}
                      onClick={() => handleAnswer(choice)}
                      data-testid={`answer-option-${choice}`}
                      style={{
                        padding: '1rem',
                        borderRadius: '8px',
                        border: `2px solid ${currentAnswer?.answer === choice ? '#0030b3' : '#ddd'}`,
                        background: currentAnswer?.answer === choice ? '#e6f0ff' : 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (currentAnswer?.answer !== choice) {
                          e.currentTarget.style.borderColor = '#999';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentAnswer?.answer !== choice) {
                          e.currentTarget.style.borderColor = '#ddd';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <span style={{ fontWeight: '600', marginRight: '0.75rem', color: '#333' }}>{choice}</span>
                        <div style={{ flex: 1 }}>
                          <MathText>{optionText}</MathText>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e5e5' }}>
              <Button
                onClick={prevQuestion}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                data-testid="prev-question-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <ChevronLeft size={20} />
                Previous
              </Button>
              
              {currentQuestionIndex === questions.length - 1 ? (
                <Button
                  onClick={() => setShowSubmitDialog(true)}
                  data-testid="submit-practice-btn"
                  style={{
                    background: '#0030b3',
                    color: 'white'
                  }}
                >
                  Submit Practice
                </Button>
              ) : (
                <Button
                  onClick={nextQuestion}
                  data-testid="next-question-btn"
                  style={{
                    background: '#0030b3',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Next
                  <ChevronRight size={20} />
                </Button>
              )}
            </div>

            {/* Hint Button */}
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e5e5' }}>
              <Button
                onClick={() => {
                  const currentQ = questions[currentQuestionIndex];
                  const hintText = currentQ.hint || "Try breaking down the problem step by step. Look for key information in the question and think about what formula or concept applies.";
                  
                  setShowHint(prev => ({ ...prev, [currentQ.id]: true }));
                  setHints(prev => ({ ...prev, [currentQ.id]: hintText }));
                }}
                variant="outline"
                data-testid="hint-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: showHint[currentQuestion?.id] ? '#fef3c7' : 'white',
                  borderColor: '#f59e0b'
                }}
              >
                <Lightbulb size={20} color="#f59e0b" />
                {showHint[currentQuestion?.id] ? 'Hint Shown' : 'Show Hint'}
              </Button>

              {/* Display Hint */}
              {showHint[currentQuestion?.id] && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '0.75rem'
                }}>
                  <Lightbulb size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                  <div style={{ fontSize: '0.95rem', color: '#78350f', lineHeight: '1.5' }}>
                    {(() => {
                      const hintText = hints[currentQuestion?.id];
                      const questionHint = currentQuestion?.hint;
                      const displayHint = hintText || questionHint;
                      
                      if (displayHint) {
                        return (
                          <>
                            <strong>Hint:</strong>{' '}
                            <MathText>{displayHint}</MathText>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <strong>Hint:</strong> <span style={{color: '#ef4444'}}>No hint available</span>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Calculator Button */}
      <button
        onClick={() => setShowCalculator(true)}
        data-testid="calculator-btn"
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s, box-shadow 0.2s',
          zIndex: 999
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
        }}
        title="Open Calculator"
      >
        <Calculator size={28} color="white" />
      </button>

      <Footer />

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Practice?</DialogTitle>
            <DialogDescription>
              You have answered {Object.keys(answers).length} of {questions.length} questions.
              {Object.keys(answers).length < questions.length && (
                <span style={{ color: '#dc2626', display: 'block', marginTop: '0.5rem' }}>
                  Warning: {questions.length - Object.keys(answers).length} question(s) are unanswered.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button 
              variant="outline" 
              onClick={() => setShowSubmitDialog(false)}
              style={{ flex: 1 }}
            >
              Continue Practice
            </Button>
            <Button 
              onClick={() => {
                setShowSubmitDialog(false);
                handleSubmit();
              }}
              disabled={loading}
              style={{ flex: 1, background: '#0030b3', color: 'white' }}
              data-testid="confirm-submit-btn"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exit Practice?</DialogTitle>
            <DialogDescription>
              Your progress will be lost. Are you sure you want to exit?
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button 
              variant="outline" 
              onClick={() => setShowExitDialog(false)}
              style={{ flex: 1 }}
            >
              Continue Practice
            </Button>
            <Button 
              onClick={handleExit}
              style={{ flex: 1, background: '#dc2626', color: 'white' }}
              data-testid="confirm-exit-btn"
            >
              Exit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calculator Dialog */}
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent style={{ 
          maxWidth: 'min(900px, 95vw)', 
          width: '95vw',
          height: 'min(700px, 85vh)', 
          maxHeight: '85vh',
          padding: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <DialogHeader style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <DialogTitle>Desmos Calculator</DialogTitle>
            <DialogDescription>
              Choose between scientific or graphing calculator
            </DialogDescription>
            {/* Calculator Type Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                onClick={() => setCalculatorType('scientific')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: calculatorType === 'scientific' ? '#667eea' : '#e5e7eb',
                  color: calculatorType === 'scientific' ? 'white' : '#666',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Scientific
              </button>
              <button
                onClick={() => setCalculatorType('graphing')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: calculatorType === 'graphing' ? '#667eea' : '#e5e7eb',
                  color: calculatorType === 'graphing' ? 'white' : '#666',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Graphing
              </button>
            </div>
          </DialogHeader>
          <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
            <iframe
              src={calculatorType === 'scientific' 
                ? 'https://www.desmos.com/scientific' 
                : 'https://www.desmos.com/calculator'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={`Desmos ${calculatorType === 'scientific' ? 'Scientific' : 'Graphing'} Calculator`}
              key={calculatorType}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
